import { SessionEvent } from './../Session/SessionEvent';
import { StorageKey } from './../Storage/StorageKeys';
import { ApplicationStage } from './../Application/ApplicationStage';
import { convertTimestampToMilliseconds } from '@standardnotes/utils';
import { ApplicationEvent } from './../Event/ApplicationEvent';
import { AbstractService } from '../Service/AbstractService';
import { getErrorFromErrorResponse, isErrorResponse, } from '@standardnotes/responses';
import { SubscriptionManagerEvent } from './SubscriptionManagerEvent';
import { c } from 'ttag';
export class SubscriptionManager extends AbstractService {
    constructor(subscriptionApiService, sessions, storage, isApplicationUsingThirdPartyHostUseCase, internalEventBus) {
        super(internalEventBus);
        this.subscriptionApiService = subscriptionApiService;
        this.sessions = sessions;
        this.storage = storage;
        this.isApplicationUsingThirdPartyHostUseCase = isApplicationUsingThirdPartyHostUseCase;
        this.internalEventBus = internalEventBus;
    }
    async handleEvent(event) {
        switch (event.type) {
            case ApplicationEvent.Launched: {
                void this.fetchOnlineSubscription();
                const isThirdPartyHostUsedOrError = this.isApplicationUsingThirdPartyHostUseCase.execute();
                if (isThirdPartyHostUsedOrError.isFailed()) {
                    break;
                }
                const isThirdPartyHostUsed = isThirdPartyHostUsedOrError.getValue();
                if (!isThirdPartyHostUsed) {
                    void this.fetchAvailableSubscriptions();
                }
                break;
            }
            case ApplicationEvent.UserRolesChanged:
            case SessionEvent.Restored:
            case ApplicationEvent.SignedIn:
                void this.fetchOnlineSubscription();
                break;
            case ApplicationEvent.ApplicationStageChanged: {
                const stage = event.payload.stage;
                if (stage === ApplicationStage.StorageDecrypted_09) {
                    this.loadSubscriptionFromStorage();
                }
            }
        }
    }
    loadSubscriptionFromStorage() {
        this.onlineSubscription = this.storage.getValue(StorageKey.Subscription);
        void this.notifyEvent(SubscriptionManagerEvent.DidFetchSubscription);
    }
    hasOnlineSubscription() {
        return this.onlineSubscription != undefined;
    }
    getOnlineSubscription() {
        return this.onlineSubscription;
    }
    getAvailableSubscriptions() {
        return this.availableSubscriptions;
    }
    get userSubscriptionName() {
        if (!this.onlineSubscription) {
            throw new Error('Attempting to get subscription name without a subscription.');
        }
        if (this.availableSubscriptions &&
            this.availableSubscriptions[this.onlineSubscription.planName]) {
            return this.availableSubscriptions[this.onlineSubscription.planName].name;
        }
        return '';
    }
    get userSubscriptionExpirationDate() {
        if (!this.onlineSubscription) {
            return undefined;
        }
        return new Date(convertTimestampToMilliseconds(this.onlineSubscription.endsAt));
    }
    get isUserSubscriptionExpired() {
        if (!this.onlineSubscription) {
            throw new Error('Attempting to check subscription expiration without a subscription.');
        }
        if (!this.userSubscriptionExpirationDate) {
            return false;
        }
        return this.userSubscriptionExpirationDate.getTime() < new Date().getTime();
    }
    get isUserSubscriptionCanceled() {
        if (!this.onlineSubscription) {
            throw new Error('Attempting to check subscription expiration without a subscription.');
        }
        return this.onlineSubscription.cancelled;
    }
    async acceptInvitation(inviteUuid) {
        try {
            const result = await this.subscriptionApiService.acceptInvite(inviteUuid);
            if (isErrorResponse(result)) {
                return { success: false, message: getErrorFromErrorResponse(result).message };
            }
            return result.data;
        }
        catch {
            return {
                success: false,
                message: c('B7.FilesSubscriptionHelp.Subscription.Error').t `Could not accept invitation.`,
            };
        }
    }
    async listSubscriptionInvitations() {
        var _a;
        try {
            const result = await this.subscriptionApiService.listInvites();
            if (isErrorResponse(result)) {
                return [];
            }
            return (_a = result.data.invitations) !== null && _a !== void 0 ? _a : [];
        }
        catch {
            return [];
        }
    }
    async inviteToSubscription(inviteeEmail) {
        try {
            const result = await this.subscriptionApiService.invite(inviteeEmail);
            if (isErrorResponse(result)) {
                return false;
            }
            return result.data.success === true;
        }
        catch {
            return false;
        }
    }
    async cancelInvitation(inviteUuid) {
        try {
            const result = await this.subscriptionApiService.cancelInvite(inviteUuid);
            if (isErrorResponse(result)) {
                return false;
            }
            return result.data.success === true;
        }
        catch {
            return false;
        }
    }
    async fetchOnlineSubscription() {
        if (!this.sessions.isSignedIn()) {
            return;
        }
        try {
            const result = await this.subscriptionApiService.getUserSubscription({ userUuid: this.sessions.userUuid });
            if (isErrorResponse(result)) {
                return;
            }
            const subscription = result.data.subscription;
            this.handleReceivedOnlineSubscriptionFromServer(subscription);
        }
        catch (error) {
            void error;
        }
    }
    handleReceivedOnlineSubscriptionFromServer(subscription) {
        this.onlineSubscription = subscription;
        this.storage.setValue(StorageKey.Subscription, subscription);
        void this.notifyEvent(SubscriptionManagerEvent.DidFetchSubscription);
    }
    async fetchAvailableSubscriptions() {
        try {
            const response = await this.subscriptionApiService.getAvailableSubscriptions();
            if (isErrorResponse(response)) {
                return;
            }
            this.availableSubscriptions = response.data;
        }
        catch (error) {
            void error;
        }
    }
    async confirmAppleIAP(params, subscriptionToken) {
        try {
            const result = await this.subscriptionApiService.confirmAppleIAP({
                ...params,
                subscription_token: subscriptionToken,
            });
            if (isErrorResponse(result)) {
                return { success: false, message: getErrorFromErrorResponse(result).message };
            }
            return result.data;
        }
        catch {
            return { success: false, message: c('B7.FilesSubscriptionHelp.Subscription.Error').t `Could not confirm IAP.` };
        }
    }
}
